/* eslint-disable react/jsx-handler-names */
import './MortgageStupidBanner.css';

import React from 'react';
import { Modal, Input, PhoneInput, Button, Typography } from '@vtblife/uikit';

import classname from '@search/classname/src';
import { useStaticPathConfig } from '@search/common-core/src/StaticPathContext';
import { formatPhone } from '@search/helpers/src/formatPhone';

import { formatLocaleNumber } from '@search/helpers/src/formatNumber';
import { defaultMortgageRateNbFormatted, defaultMortgageRateSecondFormatted } from '../contants';
import { MortgageGovernmentDisclaimer } from '../MortgageGovernmentDisclaimer/MortgageGovernmentDisclaimer';
import { MortgageGovernmentDisclaimerModal } from '../MortgageGovernmentDisclaimer/MortgageGovernmentDisclaimerModal';
import { MortgageField } from '../domain/MortgageField';
import Image from '../../Image';
import { useGovernmentMortgage } from '../useGovernmentMortgage';
import { useStupidMortgage, MortgageStupidProps } from './useStupidMortgage';

import bannerIllustration from './bannerIllustration.url.svg';
import bankLogos from './bankLogos.url.svg';

const cn = classname.bind(null, 'MortgageDumbBanner');

export function MortgageStupidBanner(props: MortgageStupidProps & {
    onClose?: (e?: React.MouseEvent | React.KeyboardEvent) => void;
}) {
    const governmentMortgage = useGovernmentMortgage({
        propertyCost: undefined,
        regionId: props.regionId,
        isPreferentialNbCondition: props.isNewBuilding
    });
    const {
        containerRef,

        sendQueryOnEnter,
        sendQuery,

        isFormSended,
        isFormSyncing,
        nameInputValid,
        name,
        setName,
        setNameTouched,
        phoneInputValid,
        setPhone,
        phone,
        setPhoneTouched,
        loading
    } = useStupidMortgage(props);

    const {
        isMobile,
        isNewBuilding,
        minimalMortgageRate
    } = props;

    // not important all conditions government mortgage
    const title = governmentMortgage.isPreferentialNbCondition ? 'Ипотека с\u00A0господдержкой на\u00A0М2' : 'Выгодная ипотека с\u00A0М2';
    const subtitle = isNewBuilding ? 'Получите выгодное предложение от\u00A0лучших банков' : 'Получите ипотеку на\u00A0лучших условиях';

    const staticPath = useStaticPathConfig();

    const [ modal, setModal ] = React.useState({
        type: undefined as undefined | 'goverment' | 'contacts' | 'starter'
    });
    const closeModal = React.useCallback(() => setModal({ type: undefined }), []);

    const isGovermentModalOpened = modal.type === 'goverment';
    const govermentModalOpen = React.useCallback(() => setModal({ type: 'goverment' }), []);

    const isContactsModalOpened = modal.type === 'contacts';
    const contactsModalOpen = React.useCallback(() => setModal({ type: 'contacts' }), []);

    return (<>
        <div className={cn(null, { isMobile })} ref={containerRef}>
            {isMobile ? null : (
                <Image
                    loading='lazy'
                    width={242}
                    height={312}
                    alt='иллюстрация'
                    url={`${staticPath}${bannerIllustration}`}
                />
            )}
            <div className={cn('content', { isMobile })}>
                {isFormSended ? (<>
                    <Typography variant='h2' className={cn('title', { isMobile })}>
                        Заявка принята
                    </Typography>
                    <Typography
                        variant='primary-alone'
                        color='secondary'
                    >
                        Менеджер скоро свяжется с&nbsp;вами по&nbsp;номеру {formatPhone(phone, { skipSymbols: true, unbreakable: true })}
                    </Typography>
                </>) : (<>
                    <Typography
                        variant={isMobile ? 'h3' : 'h2'}
                        className={cn('title', { isMobile })}
                    >
                        {title}
                    </Typography>
                    {subtitle && (
                        <Typography
                            variant='primary-alone'
                        >
                            {subtitle}
                        </Typography>
                    )}
                    <div className={cn('description-cells', { isMobile })}>
                        <MortgageField
                            titleProps={{ resetFont: true }}
                            title={<Image
                                height={isMobile ? 48 : 52}
                                loading='lazy'
                                alt='логотипы банков'
                                url={`${staticPath}${bankLogos}`}
                            />}
                            children='более 20&nbsp;банков на&nbsp;выбор'
                        />
                        <MortgageField
                            titleProps={{ accent: true, variant: 'h3' }}
                            title={`от ${formatLocaleNumber(minimalMortgageRate) || (isNewBuilding ? defaultMortgageRateNbFormatted : defaultMortgageRateSecondFormatted)}%`}
                            children={(
                                // not important all conditions government mortgage
                                governmentMortgage.isPreferentialNbCondition ?
                                    <MortgageGovernmentDisclaimer
                                        isAccent
                                        isImportant
                                        isMobile={isMobile}
                                        descriptionText={<><Typography tag='span' color='secondary'>по&nbsp;</Typography>льготным программам</>}
                                        mainText='минимальная ставка'
                                        onClick={govermentModalOpen}
                                    /> :
                                    <>ставка по&nbsp;ипотеке <br />на&nbsp;вторичку</>
                            )}
                        />
                    </div>
                    <div className={cn('fields', { isMobile })}>
                        {isMobile ? null : <>
                            <PhoneInput
                                dataTest='mortgage-set-phone'
                                label='Номер телефона'
                                size='l'
                                inputMode='numeric'
                                onBlur={setPhoneTouched}
                                onKeyPress={sendQueryOnEnter}
                                error={phoneInputValid === false}
                                onChange={setPhone}
                                value={phone || undefined}
                                disabled={isFormSyncing}
                            />
                            <Input
                                dataTest='mortgage-set-name'
                                label='Как вас зовут'
                                placeholder='Имя'
                                size='l'
                                onBlur={setNameTouched}
                                onKeyPress={sendQueryOnEnter}
                                error={nameInputValid === false}
                                onChange={setName}
                                value={name}
                                disabled={isFormSyncing}
                            />
                        </>}
                        <Button
                            fullWidth
                            size={isMobile ? 'm' : 'l'}
                            disabled={isFormSyncing || loading}
                            dataTest='mortgage-send-query'
                            variant='accent'
                            onClick={isMobile ? contactsModalOpen : sendQuery}
                        >
                            Подать заявку
                        </Button>
                    </div>
                </>)}
            </div>
        </div>
        {isContactsModalOpened && <MortgageStupidContactsModal
            isMobile={isMobile}
            onClose={closeModal}
            isPreferentialNbCondition={governmentMortgage.isPreferentialNbCondition}
            {...props}
        />}
        {isGovermentModalOpened && <MortgageGovernmentDisclaimerModal
            isMobile={isMobile}
            onSuccess={contactsModalOpen}
            onClose={closeModal}
        />}
    </>);
}

function MortgageStupidContactsModal(props: MortgageStupidProps & {
    onClose?: () => void;
}) {
    const {
        sendQueryOnEnter,
        sendQuery,

        isFormSended,
        isFormSyncing,
        nameInputValid,
        name,
        setName,
        setNameTouched,
        phoneInputValid,
        setPhone,
        phone,
        setPhoneTouched,
        loading
    } = useStupidMortgage(props);

    const {
        isMobile,
        onClose,
        isPreferentialNbCondition
    } = props;

    // not important all conditions government mortgage
    const title = isPreferentialNbCondition ? 'Ипотека с\u00A0господдержкой на\u00A0М2' : 'Выгодная ипотека с\u00A0М2';

    return (<Modal
        opened
        onClose={onClose}
        mobile={isMobile}
        swipeToClose={! isMobile}
    >
        <Modal.Header>Заявка на&nbsp;ипотеку</Modal.Header>
        <Modal.Content>
            <div className={cn('MortgageDumbContactsModal')}>
                {isFormSended ? (<>
                    <Typography variant='h3'>
                        Заявка принята
                    </Typography>
                    <Typography variant='primary-alone'>
                        Менеджер скоро свяжется с&nbsp;вами по&nbsp;номеру {formatPhone(phone, { skipSymbols: true, unbreakable: true })}
                    </Typography>
                </>) : (<>
                    <Typography variant='h3' className={cn('title')}>
                        {title}
                    </Typography>
                    Заполните единую анкету, выберите подходящую программу и&nbsp;получите одобрение от&nbsp;лучших банков
                    <Input
                        dataTest='mortgage-set-name'
                        label='Как вас зовут'
                        placeholder='Имя'
                        size='l'
                        onBlur={setNameTouched}
                        onKeyPress={sendQueryOnEnter}
                        error={nameInputValid === false}
                        onChange={setName}
                        value={name}
                        disabled={isFormSyncing}
                    />
                    <PhoneInput
                        dataTest='mortgage-set-phone'
                        label='Телефон'
                        size='l'
                        inputMode='numeric'
                        onBlur={setPhoneTouched}
                        onKeyPress={sendQueryOnEnter}
                        error={phoneInputValid === false}
                        onChange={setPhone}
                        value={phone || undefined}
                        disabled={isFormSyncing}
                    />
                    <Button
                        fullWidth
                        size={isMobile ? 'm' : 'l'}
                        disabled={isFormSyncing || loading}
                        dataTest='mortgage-send-query'
                        variant='accent'
                        onClick={sendQuery}
                    >
                        Подать заявку на ипотеку
                    </Button>
                </>)}
            </div>
        </Modal.Content>
    </Modal>);
}
